import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Check, AlertCircle } from "lucide-react";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import "../../styles/add-to-cart.css";

interface AddToCartButtonProps {
  productId: string;
  variationId?: string;
  disabled?: boolean;
  quantity?: number;
  selectedSize?: string;
  selectedColor?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
  variant?: "default" | "icon" | "minimal";
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  productId,
  variationId,
  disabled = false,
  quantity = 1,
  selectedSize,
  selectedColor,
  onSuccess,
  onError,
  className = "",
  variant = "default",
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const { success } = useToast();
  const { t } = useTranslation();

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const resolveVariationId = async (): Promise<string | undefined> => {
    if (variationId) return variationId;

    try {
      const productRes = await BaseApi.get(
        `/products/${productId}?isActive=true`,
      );
      const firstVariation = productRes?.data?.variations?.[0];
      return firstVariation?.id || firstVariation?._id;
    } catch (error) {
      console.error("Failed to resolve default variation:", error);
      return undefined;
    }
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/signin");
      return;
    }

    if (isAdding || isAdded) return;

    setIsAdding(true);

    try {
      const resolvedVariationId = await resolveVariationId();
      const payload = {
        productId,
        quantity,
        ...(resolvedVariationId && { variationId: resolvedVariationId }),
        ...(selectedSize && { size: selectedSize }),
        ...(selectedColor && { color: selectedColor }),
      };

      const res = await BaseApi.post("/cart/items", payload);

      if (res.success) {
        setIsAdded(true);
        success(t("Product added to cart successfully!"));

        // Trigger cart badge animation
        window.dispatchEvent(
          new CustomEvent("cartUpdated", {
            detail: { action: "add", productId },
          }),
        );

        // Call success callback
        onSuccess?.();

        // Reset added state after animation
        setTimeout(() => {
          setIsAdded(false);
        }, 2000);
      } else {
        throw new Error(res.message || "Failed to add to cart");
      }
    } catch (error: any) {
      console.error("Failed to add to cart:", error);
      onError?.(error.message);
    } finally {
      setIsAdding(false);
    }
  };

  // Icon-only variant
  if (variant === "icon") {
    return (
      <button
        onClick={handleAddToCart}
        disabled={isAdding || isAdded}
        className={`add-to-cart-icon ${isAdding ? "adding" : ""} ${
          isAdded ? "added" : ""
        } ${className}`}
        aria-label={t("Add to cart")}
      >
        {isAdded ? (
          <Check className="icon-check" size={20} />
        ) : (
          <ShoppingCart size={20} />
        )}
      </button>
    );
  }

  // Minimal variant
  if (variant === "minimal") {
    return (
      <button
        onClick={handleAddToCart}
        disabled={isAdding || isAdded}
        className={`add-to-cart-minimal ${isAdding ? "adding" : ""} ${
          isAdded ? "added" : ""
        } ${className}`}
      >
        {isAdding ? (
          <span className="loader-minimal"></span>
        ) : isAdded ? (
          <>
            <Check size={18} />
            <span>{t("Added")}</span>
          </>
        ) : (
          <>
            <ShoppingCart size={18} />
            <span>{t("Add")}</span>
          </>
        )}
      </button>
    );
  }

  // Default full button variant
  return (
    <button
      onClick={handleAddToCart}
      disabled={isAdding || isAdded || disabled}
      className={`add-to-cart-button ${isAdding ? "adding" : ""} ${
        isAdded ? "added" : ""
      } ${className}`}
    >
      <span className="button-content">
        {isAdding ? (
          <>
            <span className="loader"></span>
            <span className="button-text">{t("Adding to Cart")}...</span>
          </>
        ) : isAdded ? (
          <>
            <Check className="icon-check" size={20} />
            <span className="button-text">{t("Added to Cart")}</span>
          </>
        ) : disabled ? (
          <>
            <AlertCircle className="text-red-700" size={20} />
            <span className="text-red-700">{t("Out of Stock")}</span>
          </>
        ) : (
          <>
            <ShoppingCart size={20} />
            <span className="button-text">{t("Add to Cart")}</span>
          </>
        )}
      </span>
    </button>
  );
};

export default AddToCartButton;
